import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Megaphone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** Platform-owner toggles for public-facing site UI. */
export const SiteSettingsSection = () => {
  const { api } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/settings/site');
      setEnabled(data.scroll_cta_banner_enabled !== false);
    } catch (_) { /* keep default */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (next) => {
    setSaving(true);
    setEnabled(next);
    try {
      await api.put('/admin/settings/site', { scroll_cta_banner_enabled: next });
      toast.success(next ? 'Free-trial banner enabled' : 'Free-trial banner disabled');
    } catch (e) {
      setEnabled(!next);
      toast.error(e.response?.data?.detail || 'Failed to update setting');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-zinc-200" data-testid="site-settings-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="w-5 h-5" />
          Marketing Site
        </CardTitle>
        <CardDescription>Toggles for public-facing marketing UI.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-50">
          <div className="pr-4">
            <p className="font-medium">Free-trial pop-up banner</p>
            <p className="text-sm text-zinc-500">
              Shows a floating "Start free" CTA on the landing page after visitors scroll down.
            </p>
          </div>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
          ) : (
            <Switch
              checked={enabled}
              disabled={saving}
              onCheckedChange={toggle}
              data-testid="scroll-cta-banner-toggle"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};
